import { type ISnippet } from '@principle-theorem/editor';
import {
  ICustomFormConfiguration,
  ICustomFormContent,
  IManualTransactionType,
  IPatientMetadataDisplay,
  IPrescriptionItem,
  ISterilisationCycleType,
  ISterilisationEquipment,
  ISterilisationMachine,
  ISterilisationPack,
  type IAutomationConfiguration,
  type IBrand,
  type ICalendarEventSchedule,
  type IConditionConfiguration,
  type IFeeSchedule,
  type IMultiTreatmentConfiguration,
  type IOrganisation,
  type IPractice,
  type IProduct,
  type IReferralSourceConfiguration,
  type IRole,
  type ISchedulingEventReason,
  type IStaffer,
  type ITag,
  type ITeam,
  type ITemplateDefinition,
  type ITreatmentCategory,
  type ITreatmentConfiguration,
  type ITreatmentTemplate,
  type IUser,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  asyncForEach,
  serialise,
  type DocumentReference,
  type SerialisedData,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, type Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AutomationConfiguration } from '../models/automation/automation-configuration';
import { Brand } from '../models/brand';
import { ConditionConfiguration } from '../models/clinical-charting/conditions/condition-configuration';
import { FeeSchedule } from '../models/clinical-charting/fees/fee-schedule/fee-schedule';
import { MultiTreatmentConfiguration } from '../models/clinical-charting/treatment/multi-treatment-configuration';
import { TreatmentConfiguration } from '../models/clinical-charting/treatment/treatment-configuration';
import { CustomFormConfiguration } from '../models/custom-forms/custom-form-configuration';
import { Organisation } from '../models/organisation/organisation';
import { Practice } from '../models/practice/practice';
import { Roster } from '../models/staffer/roster';
import { ManualTransactionType } from '../models/transaction/manual-transaction-type';

export interface IExportedBrand {
  brand: WithRef<IBrand>;
  practices: IExportedPractice[];
  appointmentTags: WithRef<ITag>[];
  automationConfigurations: WithRef<IAutomationConfiguration>[];
  eventTags: WithRef<ITag>[];
  patientNoteTags: WithRef<ITag>[];
  patientTags: WithRef<ITag>[];
  products: WithRef<IProduct>[];
  referralSources: WithRef<IReferralSourceConfiguration>[];
  manualTransactionTypes: WithRef<IManualTransactionType>[];
  snippets: WithRef<ISnippet>[];
  staffWithRosters: {
    staffer: WithRef<IStaffer>;
    rosters: WithRef<ICalendarEventSchedule>[];
  }[];
  teams: WithRef<ITeam>[];
  templates: WithRef<ITemplateDefinition>[];
  treatmentCategories: WithRef<ITreatmentCategory>[];
  treatmentConfigurations: WithRef<ITreatmentConfiguration>[];
  multiTreatmentConfigurations: WithRef<IMultiTreatmentConfiguration>[];
  conditionConfigurations: WithRef<IConditionConfiguration>[];
  treatmentTemplates: WithRef<ITreatmentTemplate>[];
  customFormConfigurations: {
    customFormConfiguration: WithRef<ICustomFormConfiguration>;
    history: WithRef<ICustomFormContent>[];
  }[];
  sterilisationMachines: WithRef<ISterilisationMachine>[];
  sterilisationPacks: WithRef<ISterilisationPack>[];
  sterilisationCycleTypes: WithRef<ISterilisationCycleType>[];
  sterilisationEquipment: WithRef<ISterilisationEquipment>[];
  prescriptionItems: WithRef<IPrescriptionItem>[];
  cancellationReasons: WithRef<ISchedulingEventReason>[];
  patientMetadataDisplays: WithRef<IPatientMetadataDisplay>[];
}

export interface IExportedOrganisation {
  organisation: WithRef<IOrganisation>;
  feeSchedules: WithRef<IFeeSchedule>[];
  brands: IExportedBrand[];
  users: WithRef<IUser>[];
  roles: WithRef<IRole>[];
}

export interface IExportedPractice {
  practice: WithRef<IPractice>;
  feeSchedules: WithRef<IFeeSchedule>[];
  mediaTags: WithRef<ITag>[];
}

export class PracticeMigrationOrganisationExporter {
  getData$(
    organisationRef: DocumentReference<IOrganisation>
  ): Observable<SerialisedData<IExportedOrganisation>> {
    const brandExporter = new PracticeMigrationBrandExporter();
    return combineLatest([
      Firestore.getDoc(organisationRef),
      Firestore.getDocs(Organisation.brandCol({ ref: organisationRef })),
    ]).pipe(
      switchMap(async ([organisation, brands]) =>
        serialise({
          organisation,
          brands: await asyncForEach(brands, (brand) =>
            brandExporter.transformBrandForExport(brand)
          ),
          feeSchedules: await Firestore.getDocs(FeeSchedule.col(organisation)),
          users: await Firestore.getDocs(Organisation.userCol(organisation)),
          roles: await Firestore.getDocs(Organisation.roleCol(organisation)),
        })
      )
    );
  }
}

export class PracticeMigrationBrandExporter {
  getData$(
    brandRef: DocumentReference<IBrand>
  ): Observable<SerialisedData<IExportedBrand>> {
    return Firestore.doc$(brandRef).pipe(
      switchMap(async (brand) => {
        return serialise(await this.transformBrandForExport(brand));
      })
    );
  }

  async transformBrandForExport(
    brand: WithRef<IBrand>
  ): Promise<IExportedBrand> {
    const appointmentTags = await Firestore.getDocs(
      Brand.appointmentTagCol(brand)
    );
    const automationConfigurations = await Firestore.getDocs(
      AutomationConfiguration.col(brand)
    );
    const eventTags = await Firestore.getDocs(Brand.eventTagCol(brand));
    const patientNoteTags = await Firestore.getDocs(
      Brand.patientNoteTagCol(brand)
    );
    const patientTags = await Firestore.getDocs(Brand.patientTagCol(brand));

    const practiceExporter = new PracticeMigrationPracticeExporter();
    const practices = await asyncForEach(
      await Firestore.getDocs(Brand.practiceCol(brand)),
      (practice) => practiceExporter.transformPracticeForExport(practice)
    );

    const products = await Firestore.getDocs(Brand.productCol(brand));
    const referralSources = await Firestore.getDocs(
      Brand.referralSourceCol(brand)
    );
    const manualTransactionTypes = await Firestore.getDocs(
      ManualTransactionType.col(brand)
    );
    const snippets = await Firestore.getDocs(Brand.snippetCol(brand));
    const staff = await Firestore.getDocs(Brand.stafferCol(brand));
    const staffWithRosters = await asyncForEach(staff, async (staffer) => {
      const rosters = await Firestore.getDocs(Roster.col(staffer));
      return {
        staffer,
        rosters,
      };
    });
    const teams = await Firestore.getDocs(Brand.teamCol(brand));
    const templates = await Firestore.getDocs(Brand.templateCol(brand));
    const treatmentCategories = await Firestore.getDocs(
      Brand.treatmentCategoryCol(brand)
    );
    const treatmentConfigurations = await Firestore.getDocs(
      TreatmentConfiguration.col(brand)
    );
    const multiTreatmentConfigurations = await Firestore.getDocs(
      MultiTreatmentConfiguration.col(brand)
    );
    const conditionConfigurations = await Firestore.getDocs(
      ConditionConfiguration.col(brand)
    );
    const treatmentTemplates = await Firestore.getDocs(
      Brand.treatmentTemplateCol(brand)
    );
    const customFormConfigurations = await asyncForEach(
      await Firestore.getDocs(Brand.customFormConfigCol(brand)),
      async (customFormConfiguration) => {
        return {
          customFormConfiguration,
          history: await Firestore.getDocs(
            CustomFormConfiguration.contentHistoryCol(customFormConfiguration)
          ),
        };
      }
    );

    const sterilisationMachines = await Firestore.getDocs(
      Brand.sterilisationMachineCol(brand)
    );

    const sterilisationPacks = await Firestore.getDocs(
      Brand.sterilisationPacksCol(brand)
    );

    const sterilisationCycleTypes = await Firestore.getDocs(
      Brand.sterilisationCycleTypesCol(brand)
    );

    const sterilisationEquipment = await Firestore.getDocs(
      Brand.sterilisationEquipmentCol(brand)
    );

    const prescriptionItems = await Firestore.getDocs(
      Brand.prescriptionItemCol(brand)
    );

    const cancellationReasons = await Firestore.getDocs(
      Brand.cancellationReasonCol(brand)
    );

    const patientMetadataDisplays = await Firestore.getDocs(
      Brand.patientMetadataDisplayCol(brand)
    );

    return {
      brand,
      appointmentTags,
      automationConfigurations,
      eventTags,
      patientNoteTags,
      patientTags,
      practices,
      products,
      referralSources,
      manualTransactionTypes,
      snippets,
      teams,
      templates,
      treatmentCategories,
      treatmentConfigurations,
      multiTreatmentConfigurations,
      conditionConfigurations,
      treatmentTemplates,
      staffWithRosters,
      customFormConfigurations,
      sterilisationMachines,
      sterilisationPacks,
      sterilisationCycleTypes,
      sterilisationEquipment,
      prescriptionItems,
      cancellationReasons,
      patientMetadataDisplays,
    };
  }
}

export class PracticeMigrationPracticeExporter {
  getData$(
    practiceRef: DocumentReference<IPractice>
  ): Observable<SerialisedData<IExportedPractice>> {
    return Firestore.doc$(practiceRef).pipe(
      switchMap(async (practice) => {
        return serialise(await this.transformPracticeForExport(practice));
      })
    );
  }

  async transformPracticeForExport(
    practice: WithRef<IPractice>
  ): Promise<IExportedPractice> {
    const feeSchedules = await Firestore.getDocs(FeeSchedule.col(practice));
    const mediaTags = await Firestore.getDocs(Practice.mediaTagCol(practice));
    return {
      practice,
      feeSchedules,
      mediaTags,
    };
  }
}
